import React from "react";
import { ShareSection } from '../../components/template';


const hashtagsBottom = [
  "#deepwork",
  "#dharma",
  "#attentioneconomy",
  "#focus",
  "#concentration",
  "#ekagrata",
  "#yogicwisdom",
  "#clarity",  "#digitaldetox",
  "#mindfulness",
  "#socialmedia",

];

// Reusable HashtagSection component with consistent styling
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog3 = () => {
  return (
    <div className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed">
      {/* Header */}
      <h1 className="text-secondary text-center text-3xl font-bold mb-8">
        “Just 5 Minutes on Instagram” — And Other Lies We Tell Ourselves
      </h1>

      {/* Header Image */}
      <img
        src="/assets/knowyourself/blogs/blog3/b3p1.png"
        alt="Illustrative image for the blog"
        className="w-[80%] max-w-xl h-auto max-h-[250px] rounded-lg shadow-md mx-auto object-cover mb-8"
      />


      {/* Introduction */}
      <section className="space-y-4 mb-8">
        <p>
          Ever opened Instagram “just to check one message”…
        </p>
        <p>
          And suddenly it's been 47 minutes, 12 reels, and you’re now watching a cat make biryani?
        </p>
        <p>Don’t worry. We’ve all been there.</p>
        <p>
          In this hyper-connected world, our attention is being sold like pakoras on a rainy day.
        </p>
        <p>But here’s the problem —</p>
        <p>
          We are the generation with <strong className="text-[#578d48] font-semibold">limitless information… and zero concentration.</strong>
        </p>
        <p>
          Our devices are glowing. But our willpower? Flickering.
        </p>
      </section>

      {/* The Science of Scattered Minds */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          The Science of Scattered Minds
        </h2>
        <img
          src="/assets/knowyourself/blogs/blog3/b3p2.png"
          alt="Illustrative image for the blog"
          className="w-[80%] max-w-xl h-auto max-h-[250px] rounded-lg shadow-md mx-auto object-cover mb-8"
        />
        <p>
          Did you know? Every time you get a notification, your brain gets a hit of dopamine — 
          the same chemical linked to <em>addiction of smoking, drinking, and other substances.</em>
        </p>
        <p>And like a good junkie, you keep coming back for more.</p>
        <p>What’s happening behind the scenes:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Weakened Prefrontal Cortex –</strong> the seat of willpower and decision-making
          </li>
          <li>
            <strong>Reduced Gray Matter – </strong>in areas linked to empathy and self-regulation
          </li>
          <li>
            <strong>Sleep Disruption – </strong>thanks to blue light + late-night scrolling
          </li>
          <li>
            <strong>Increased Anxiety + Mood Swings – </strong>from overexposure to bad news, unrealistic reels, and toxic comparison
          </li>
        </ul>
        <p>
          It’s not just about “screen time.”<br />
          It’s about <strong className="text-[#578d48] font-semibold">energy leaks. Emotional clutter. Mental fog.</strong>
        </p>
      </section>

      {/* The Real Cost: Dreams, Focus, Dharma */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-3xl mt-2 font-semibold">
          The Real Cost: Dreams, Focus, Dharma
        </h2>
        <img
          src="/assets/knowyourself/blogs/blog3/b3p3.png"
          alt="Illustrative image for the blog"
          className="w-[80%] max-w-xl h-auto max-h-[250px] rounded-lg shadow-md mx-auto object-cover mb-8"
        />
        <p>
          Remember when you had that business idea?<br />
          That book you wanted to write?<br />
          That yoga practice you meant to start?
        </p>
        <p>
          All quietly buried… under a pile of memes and mindless swiping.
        </p>
        <p>
          As Cal Newport says in Deep Work:<br />
          “The ability to perform deep work is becoming increasingly rare at exactly the same time it is becoming increasingly valuable.”
        </p>
        <p>
          Modern distractions are killing the ancient power of <strong className="text-[#578d48] font-semibold">ekagrata (one-pointed focus).</strong>
        </p>
        <p>
          The Bhagavad Gita nails it:<br />
          <strong className="text-[#578d48] font-semibold"> “Vyavasāyātmikā buddhir ekeha kuru-nandana…”</strong><br />
          Those who are resolute in purpose have one-pointed focus. The intelligence of the indecisive is many-branched.
        </p>
        <p>Translation?</p>
        <p>
          If you want to do anything meaningful in life,<br />
          <strong>scrolling endlessly ain't it.</strong>
        </p>
      </section>

      {/* From Scattered to Centred: The Yogic View */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          From Scattered to Centred: The Yogic View
        </h2>
        <p>
          In Yoga and Vedanta, the mind is seen like a lake.
        </p>
        <p>
          Every ping, post, reel, or like — is a pebble thrown into that lake.
        </p>
        <p>
          More ripples = Less reflection = No clarity.
        </p>
        <p>
          Want shanti?<br />
          You have to make that lake still again.
        </p>
        <p>
          This is why ancient yogis practiced:
        </p>
        <ul className="list-disc text-[#578d48] font-semibold list-inside space-y-1">
          <li>
            <strong>Mauna (silence)</strong>
          </li>
          <li>
            <strong>Pratyahara (withdrawal from external distractions)</strong>
          </li>
        </ul>
        <p>
          They weren’t antisocial — they were laser-focused.<br />
          Living proof that you don’t need constant input to feel full.
        </p>
      </section>

      {/* Shocking Digital Stats */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          Shocking Digital Stats (Brace Yourself)
        </h2>
        <ul className="list-disc list-inside space-y-1">
          <li>The average person checks their phone <strong className="text-[#578d48] font-semibold">96 times a day</strong></li>
          <li>Teenagers spend over <strong className="text-[#578d48] font-semibold">7 hours/day</strong> on screens</li>
          <li>Social media increases risk of <strong className="text-[#578d48] font-semibold">depression by 70%</strong> in heavy users</li>
          <li>It takes <strong className="text-[#578d48] font-semibold">23 minutes</strong> to regain focus after one distraction (yes, even one notification!)</li>
        </ul>
        <p>
          This isn’t just bad time management.<br />
          It’s a full-blown spiritual hemorrhage.
        </p>
      </section>

      {/* Digital Survival Toolkit */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          So, What Can We Do? (Without Throwing Our Phones Off a Cliff)
        </h2>
        <img
          src="/assets/knowyourself/blogs/blog3/b3p4.png"
          alt="Illustrative image for the blog"
          className="w-[80%] max-w-xl h-auto max-h-[250px] rounded-lg shadow-md mx-auto object-cover mb-8"
        />
        <p>
          Here’s your digital survival toolkit — tried, tested, yogi-approved.
        </p>
        <p>
          <strong>1. 📵 Digital Fasting</strong><br />
          Pick 1 day/week or 2 hours/day to go completely screen-free.<br />
          Let your nervous system breathe.
        </p>
        <p>
          <strong>2. 🧘 Mindful Usage</strong><br />
          Before opening an app, ask: Why am I using this? What do I hope to gain?
        </p>
        <p>
          <strong>3. 🕯️ Start the Day in Silence</strong><br />
          No phones for the first hour. Journal. Meditate. Breathe.<br />
          Don’t let your mind be hijacked before sunrise.
        </p>
        <p>
          <strong>4. ✂️ Unfollow Ruthlessly</strong><br />
          Declutter your digital diet. If it doesn't uplift, educate, or inspire — cut it off like toxic aloo from biryani.
        </p>
        <p>
          <strong>5. 🔕 Notifications = No No</strong><br />
          Turn off all unnecessary pings. Your focus deserves better.
        </p>
      </section>

      {/* Call to Action */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          Call to Action: Your Digital Detox Starts NOW
        </h2>
        <p>
          No, you don’t need to live in a cave.<br />
          But you do need to <strong className="text-[#578d48] font-semibold">reclaim your mind </strong>before it becomes public property.
        </p>
        <p>
          Want a guided 7-day <strong>Digital Detox Protocol?</strong><br />
          We’ve got one. Holistic, practical, and built for your lifestyle.
        </p>
        <p>
          <a href="/PARAM digital detox Guide 2025.pdf">
            👉 <strong className="underline">Download Digital Detox Guide</strong>
          </a>
        </p>
        <p>
          Let’s bring back our attention from the thieves of time.
        </p>
      </section>

      {/* Final Truth Bomb */}
      <section className="space-y-4 mb-8">
        <h2 className="text-primary text-2xl font-semibold">
          🧘 Final Truth Bomb
        </h2>
        <p>Your attention is sacred.</p>
        <p>Your willpower is a muscle.</p>
        <p>
          And your mind was not designed to process 10,000 reels a day.
        </p>
        <p>
          The Gita doesn’t say: “Be always connected.”<br />
          It says:
        </p>
        <p className="whitespace-pre-line">
          <strong className="text-secondary font-semibold">
            “Uddhared ātmanātmānaṁ…”
          </strong>
        </p>
        <p>
          <em className="text-[#578d48] font-semibold">Lift yourself by your own Self.</em>
        </p>
        <p>
          So today, unplug to plug in —<br />
          To your goals, your growth, your grounded greatness.
        </p>
        <p>
          Because you were meant for more than likes, swipes, and distractions.<br />
          You were meant for <strong className="text-[#578d48] font-semibold">dharma. deep work. and divine impact.</strong>
        </p>
        <p>
          Time to log out — and tune in. 🙏📴
        </p>
      </section>

      {/* Hashtags at the Bottom */}
      <HashtagSection tags={hashtagsBottom} />
      <ShareSection/>
    </div>
  );
};

export default Blog3;
